import { Handle, type NodeProps, Position } from "@xyflow/react";
import { AlertTriangle } from "lucide-react";
import type { NodeDef } from "../../types/flows";
import { HelpPopover } from "./NodeHelp";
import { accentOf, NodeIcon } from "./ui";

// One node component for both canvases. The editor passes `issues`, the run
// inspector passes `runStatus` and counts; neither knows about the other.
//
// Handles are drawn from the catalogue's `inputs`/`outputs`, so Branch's
// true/false and Validate's pass/fail appear because the registry says they
// exist, not because this file lists them.

export interface FlowNodeData extends Record<string, unknown> {
	def?: NodeDef;
	label: string;
	summary: string;
	accent: string;
	issues?: string[];
	runStatus?: string;
	inputCount?: number | null;
	outputCount?: number | null;
	readOnly?: boolean;
}

const RUN_TONES: Record<string, string> = {
	running: "bg-sky-100 text-sky-700",
	succeeded: "bg-emerald-100 text-emerald-700",
	failed: "bg-rose-100 text-rose-700",
	skipped: "bg-slate-100 text-slate-500",
	pending: "bg-slate-100 text-slate-400",
	waiting_approval: "bg-violet-100 text-violet-700",
};

const handleStyle = {
	width: 9,
	height: 9,
	background: "#94a3b8",
	border: "none",
};

export const FlowNode = ({ data, selected }: NodeProps) => {
	const node = data as FlowNodeData;
	const tone = accentOf(node.accent);
	const inputs = node.def?.inputs ?? [];
	const outputs = node.def?.outputs ?? [];
	const hasIssues = (node.issues?.length ?? 0) > 0;

	return (
		<div
			className={`relative w-60 rounded-xl border-2 bg-white shadow-sm transition-shadow ${
				selected
					? "border-primary shadow-md"
					: hasIssues
						? "border-danger/50"
						: tone.border
			}`}
		>
			{inputs.map((handle, index) => (
				<Handle
					key={handle.id}
					type="target"
					position={Position.Left}
					id={handle.id}
					style={{
						...handleStyle,
						top:
							inputs.length === 1
								? "50%"
								: `${((index + 1) / (inputs.length + 1)) * 100}%`,
					}}
				/>
			))}

			<div
				className={`flex items-center gap-2 rounded-t-lg px-3 py-2 ${tone.bg}`}
			>
				<span className={tone.text}>
					<NodeIcon name={node.def?.icon ?? "Circle"} className="h-4 w-4" />
				</span>
				<span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-slate-800">
					{node.label}
				</span>
				{node.def && <HelpPopover def={node.def} accent={node.accent} />}
			</div>

			<div className="space-y-1.5 px-3 py-2">
				<p className="truncate text-[11px] text-slate-500" title={node.summary}>
					{node.summary}
				</p>

				{node.runStatus && (
					<div className="flex items-center gap-1.5">
						<span
							className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
								RUN_TONES[node.runStatus] ?? "bg-slate-100 text-slate-500"
							}`}
						>
							{node.runStatus === "waiting_approval"
								? "waiting"
								: node.runStatus}
						</span>
						{node.outputCount !== null && node.outputCount !== undefined && (
							<span className="text-[10px] text-slate-400">
								{node.inputCount ?? 0} in · {node.outputCount} out
							</span>
						)}
					</div>
				)}

				{hasIssues && (
					<p
						className="flex items-start gap-1 text-[10px] text-danger"
						title={node.issues?.join("\n")}
					>
						<AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
						{node.issues?.length} problem{node.issues?.length === 1 ? "" : "s"}
					</p>
				)}
			</div>

			{outputs.map((handle, index) => (
				<div key={handle.id}>
					<Handle
						type="source"
						position={Position.Right}
						id={handle.id}
						style={{
							...handleStyle,
							top:
								outputs.length === 1
									? "50%"
									: `${((index + 1) / (outputs.length + 1)) * 100}%`,
						}}
					/>
					{outputs.length > 1 && (
						<span
							className="absolute -right-1 translate-x-full text-[9px] font-medium text-slate-400"
							style={{
								top: `calc(${((index + 1) / (outputs.length + 1)) * 100}% - 6px)`,
							}}
						>
							{handle.label}
						</span>
					)}
				</div>
			))}
		</div>
	);
};

export const nodeTypes = { flowNode: FlowNode };
